/**
 * RetentionSection - Data retention preference selector
 *
 * Self-contained component (like LanguageSection) for configuring
 * how long analysis data is retained before auto-deletion.
 * Follows the LanguageSection multi-button pattern with selected indicator.
 *
 * M4.3: Configurable Data Retention Policies (GDPR Art. 5(1)(e))
 */
import { View, ActivityIndicator } from "react-native";
import { useTranslation } from "@dramadetector/shared";
import { TierBody } from "@dramadetector/ui";
import { GlassCard } from "../../../components/glass";
import { SettingsItem } from "../../../components/SettingsItem";
import {
  useRetentionPreference,
  type RetentionDays,
} from "../../../hooks/useRetentionPreference";

const RETENTION_OPTIONS: { days: RetentionDays; labelKey: string }[] = [
  { days: 30, labelKey: "retention.days_30" },
  { days: 60, labelKey: "retention.days_60" },
  { days: 90, labelKey: "retention.days_90" },
  { days: 180, labelKey: "retention.days_180" },
  { days: 365, labelKey: "retention.days_365" },
];

export function RetentionSection() {
  const { t } = useTranslation("settings");
  const {
    retentionDays,
    setRetentionDays,
    isLoading,
    isUpdating,
    isAuthenticated,
  } = useRetentionPreference();

  if (!isAuthenticated) return null;

  return (
    <View className="mt-4">
      <TierBody
        size="xs"
        weight="semibold"
        className="text-gray-500 uppercase px-4 mb-2"
      >
        {t("sections.retention")}
      </TierBody>
      <GlassCard>
        <SettingsItem
          icon="🗓️"
          label={t("retention.title")}
          description={t("retention.description")}
          testID="retention-section-header"
        />
        {isLoading ? (
          <View className="py-4 items-center">
            <ActivityIndicator size="small" color="#A855F7" />
          </View>
        ) : (
          RETENTION_OPTIONS.map((option, index) => (
            <View key={option.days}>
              <View className="border-t border-gray-200" />
              <SettingsItem
                label={t(option.labelKey as any)}
                onPress={() => setRetentionDays(option.days)}
                selected={retentionDays === option.days}
                testID={`retention-option-${option.days}`}
              />
            </View>
          ))
        )}
        {isUpdating && (
          <View className="py-2 items-center">
            <TierBody size="xs" className="text-gray-400">
              {t("retention.updating")}
            </TierBody>
          </View>
        )}
      </GlassCard>
    </View>
  );
}
